import { captureException } from '@sentry/react';
import { injector } from 'base/dependency-container';
import { LibraryCache } from 'base/library_cache';
import {
  extractSimpleElementState,
  extractSimpleSectionState,
  SimpleSectionState,
} from 'base/react_integrations';
import { Button } from 'base/ui/button';
import { openDialog } from 'base/ui/dialog';
import { TextRegularLight } from 'base/ui/typography';
import { downloadZip } from 'client-zip';
import { observable } from 'mobx';
import React from 'react';
import { ElementInstanceState, Page, Project } from './model';
import { unrefSection, walkProject } from './model_helpers';
import { SelectionStore } from './selection/store';

type Exporter = {
  canExport: (slug: string) => boolean | undefined; // undefined means unknown (happens when the module is loading)
  exportSource: (section: SimpleSectionState, name?: string) => Promise<string>;
  exportHtml: (section: SimpleSectionState) => Promise<string>;
};

function createExporter(): Exporter {
  const modulePromise = import('components/exporter');
  const exporter: Exporter = observable({
    canExport: () => undefined,
    exportSource: async (section, name) => {
      return (await modulePromise).exportSectionAsSource(section, name);
    },
    exportHtml: async (section) => {
      return (await modulePromise).exportSectionAsHtml(section);
    },
  });
  modulePromise.then((module) => {
    exporter.canExport = (slug) => module.supportsSection(slug);
  });
  return exporter;
}

function renderPage(components: string[]): string {
  const imports = components
    .map(
      (componentName) =>
        `import { ${componentName} } from "./components/${componentName}";\n`,
    )
    .join('');
  const elements = components
    .map((componentName) => `      <${componentName} />`)
    .join('\n');
  return `import React from "react";\n${imports}
export default function Page() {
  return (
    <div>
${elements}
    </div>
  );
}
`;
}

function asyncErrorToUndefined(e: unknown): undefined {
  console.error(e);
  captureException(e);
  return undefined;
}

export function canExport(slug: string) {
  return injector.resolve(createExporter).canExport(slug);
}

export async function exportSectionSource(
  format: 'react' | 'html',
  rootElement: ElementInstanceState,
  library: LibraryCache,
) {
  const simpleSectionState = await extractSimpleElementState(
    rootElement,
    library,
  );
  if (format === 'react') {
    return await injector
      .resolve(createExporter)
      .exportSource(
        {
          element: simpleSectionState,
          slug: rootElement.source.slug,
        },
        slugToComponentName(rootElement.source.slug),
      )
      .catch(asyncErrorToUndefined);
  } else {
    return await injector
      .resolve(createExporter)
      .exportHtml({
        element: simpleSectionState,
        slug: rootElement.source.slug,
      })
      .catch(asyncErrorToUndefined);
  }
}

export function slugToComponentName(name: string) {
  return name
    .replace('section_', '')
    .replace(/_component$/, '')
    .split(/[ _-]/)
    .map(
      (segment) =>
        segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase(),
    )
    .join('');
}

type ExportedFile = {
  name: string;
  input: string;
};

type ExportResult = {
  files: ExportedFile[];
  componentsMissing: boolean;
};

export async function exportPageSource(
  format: 'react' | 'html',
  page: Page,
  library: LibraryCache,
  project: Project,
): Promise<ExportResult> {
  const exporter = injector.resolve(createExporter);
  const files: ExportedFile[] = [];
  let componentsMissing = false;
  if (format === 'html') {
    const input = (
      await Promise.all(
        page.sections.map(async (sectionOrRef) => {
          const section = unrefSection(sectionOrRef, project);
          if (section == null) {
            return;
          }
          const state = await extractSimpleSectionState(
            section.state,
            library,
          ).catch(asyncErrorToUndefined);
          if (state != null) {
            return exporter.exportHtml(state).catch((e) => {
              componentsMissing = true;
              return asyncErrorToUndefined(e);
            });
          }
        }),
      )
    )
      .filter((el) => el != null)
      .join('\n');
    files.push({ name: 'index.html', input });
  } else {
    const names = new Map<string, number>();
    const sections = (
      await Promise.all(
        page.sections.map(async (sectionOrRef) => {
          const section = unrefSection(sectionOrRef, project);
          if (section == null) {
            return;
          }
          let componentName = section?.element.current?.source.slug;
          if (componentName == null) {
            return;
          }
          componentName = slugToComponentName(componentName);
          if (names.has(componentName)) {
            names.set(componentName, names.get(componentName)! + 1);
            componentName += `_${names.get(componentName)}`;
          } else {
            names.set(componentName, 0);
          }

          const state = await extractSimpleSectionState(
            section.state,
            library,
          ).catch(asyncErrorToUndefined);
          if (state == null) {
            return;
          }
          const code = await exporter
            .exportSource(state, componentName)
            .catch(asyncErrorToUndefined);
          if (code == null) {
            componentsMissing = true;
            return;
          }
          files.push({
            name: `components/${componentName}.jsx`,
            input: code,
          });
          return componentName;
        }),
      )
    ).filter((el): el is string => el != null);
    if (sections.length > 0) {
      files.push({ name: 'index.jsx', input: renderPage(sections) });
    }
  }
  return { files, componentsMissing };
}

export async function exportSiteSource(
  format: 'react' | 'html',
  selection: SelectionStore,
  project: Project,
  library: LibraryCache,
): Promise<ExportResult> {
  const files: Promise<ExportedFile[]>[] = [];
  const currentSpace = selection.get().space;
  let componentsMissing = false;
  walkProject(project, (node) => {
    if (
      node.type === 'page' &&
      (node.space === currentSpace ||
        (currentSpace === 'home' && node.space == null))
    ) {
      const dirName = node.node.name
        .replace('section_', '')
        .toLowerCase()
        .replaceAll(' ', '-');
      files.push(
        exportPageSource(format, node.node, library, project).then((result) => {
          if (result.componentsMissing) {
            componentsMissing = true;
          }
          return result.files.map(({ name, input }) => ({
            name: `${dirName}/${name}`,
            input,
          }));
        }),
      );
    }
  });
  return { files: (await Promise.all(files)).flat(), componentsMissing };
}

export function showMissingComponentsWarning({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const confirm = () => {
    close();
    onConfirm();
  };
  const cancel = () => {
    close();
    onCancel();
  };
  const close = openDialog({
    title: 'Some components are not available',
    children: (
      <TextRegularLight>
        One or more components are currently not available in your selected
        export format. They will not be included in your downloaded file.
      </TextRegularLight>
    ),
    buttons: [
      <Button label="Cancel" size="large" onClick={cancel} key="cancel" />,
      <Button
        label="Continue"
        size="large"
        variant="primary"
        onClick={confirm}
        key="continue"
      />,
    ],
    onClose: cancel,
  });
}

export async function downloadTextFile(name: string, content: string) {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(new Blob([content], { type: 'text/plain' }));
  link.download = name;
  link.click();
  link.remove();
}

export async function downloadArchive(
  files: { name: string; input: string }[],
  name?: string,
) {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(await downloadZip(files).blob());
  link.download = name ? `${name}.zip` : 'export.zip';
  link.click();
  link.remove();
}
